import classnames from 'classnames';
import { CrossSvg, MicroscopeSvg } from 'components/SorraSvg';
import _ from 'lodash';
import Link from 'next/link';
import React from 'react';
import Modal from 'react-modal';

import { offset } from '@floating-ui/dom';
import {
  safePolygon,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';

import 'swiper/css';
import 'swiper/css/effect-fade';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { MixpanelConfig } from 'utils/mixpanelConfig';

import MaxWidthContainer from 'components/MaxWidthContainer';

import { useRouter } from 'next/router';
import redirectToLogin from 'utils/redirectToLogin';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useGlobalConfig } from 'contexts/GlobalConfig';
import { useUserAuth } from 'contexts/UserAuth';
import { useUserData } from 'contexts/UserData';
import useNavConfig from 'hooks/useNavConfig';
import useRWD from 'hooks/useRWD';

import SearchInput from 'components/SearchInput';
import sessionStore from 'utils/sessionStore';
import { openAppOrStore } from 'utils/app';

const { useEffect, useState, forwardRef, useRef } = React;

const FloatingCategories = forwardRef((props, ref) => {
  const { categories, style, ...restProps } = props;
  return (
    <div
      ref={ref}
      className="z-[60] grid grid-cols-3 gap-x-12 gap-y-4 rounded-lg border border-[#E7E7E7] bg-white p-4"
      style={{
        ...style,
        boxShadow: '2px 2px 5px 0px rgba(0, 0, 0, 0.10)',
      }}
      {...restProps}
    >
      {categories.map((category) => {
        const { id, name } = category;
        return (
          <Link
            prefetch={false}
            key={id}
            href={`/category/${encodeURIComponent(name)}`}
            className="flex items-center gap-x-1.5 text-sm font-medium text-[#111928]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="13"
              viewBox="0 0 14 13"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.34531 12.9001C9.0427 12.9001 10.6706 12.2258 11.8708 11.0256C13.071 9.82535 13.7453 8.19748 13.7453 6.5001C13.7453 4.80271 13.071 3.17485 11.8708 1.97461C10.6706 0.774382 9.0427 0.100098 7.34531 0.100098C5.64793 0.100098 4.02006 0.774382 2.81983 1.97461C1.6196 3.17485 0.945312 4.80271 0.945312 6.5001C0.945312 8.19748 1.6196 9.82535 2.81983 11.0256C4.02006 12.2258 5.64793 12.9001 7.34531 12.9001ZM5.90542 8.4201C5.90542 8.54738 5.95597 8.66945 6.04595 8.75946C6.13597 8.84944 6.25803 8.9 6.38531 8.9C6.51259 8.9 6.63466 8.84944 6.72467 8.75946L8.64467 6.83946C8.73466 6.74944 8.78521 6.62738 8.78521 6.5001C8.78521 6.37282 8.73466 6.25075 8.64467 6.16074L6.72467 4.24074C6.63414 4.1533 6.51289 4.10492 6.38704 4.10601C6.26119 4.10711 6.1408 4.15759 6.0518 4.24658C5.9628 4.33558 5.91232 4.45597 5.91123 4.58183C5.91014 4.70768 5.95852 4.82893 6.04595 4.91946L7.62659 6.5001L6.04595 8.08074C5.95597 8.17075 5.90542 8.29282 5.90542 8.4201Z"
                fill="#F16B7C"
              />
            </svg>
            <p>{name}</p>
          </Link>
        );
      })}
    </div>
  );
});

const MobileCategories = (props) => {
  const {
    title = '',
    categories = [],
    expand = false,
    setExpand,
    onNavigate,
  } = props;
  return (
    <div className="border-b border-[#E7E7E7] py-4">
      <div className="flex items-center font-medium">
        <p className="text-base text-[#111928]">{title}</p>
        <button
          className="ml-auto text-sm text-sorra-pink"
          onClick={() => {
            setExpand && setExpand(!expand);
          }}
        >
          {expand ? '收起' : '展開全部'}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-x-12 gap-y-4">
        {(expand ? categories : _.take(categories, 3)).map((category) => {
          const { id, name } = category;
          return (
            <Link
              prefetch={false}
              key={id}
              href={`/category/${encodeURIComponent(name)}`}
              className="flex items-center gap-x-1 text-sm font-medium text-[#111928]"
              onClick={() => {
                onNavigate && onNavigate();
              }}
            >
              <div className="shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="13"
                  viewBox="0 0 14 13"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.07578 12.9001C8.77317 12.9001 10.401 12.2258 11.6013 11.0256C12.8015 9.82535 13.4758 8.19748 13.4758 6.5001C13.4758 4.80271 12.8015 3.17485 11.6013 1.97461C10.401 0.774382 8.77317 0.100098 7.07578 0.100098C5.3784 0.100098 3.75053 0.774382 2.5503 1.97461C1.35007 3.17485 0.675781 4.80271 0.675781 6.5001C0.675781 8.19748 1.35007 9.82535 2.5503 11.0256C3.75053 12.2258 5.3784 12.9001 7.07578 12.9001ZM5.63588 8.4201C5.63588 8.54738 5.68644 8.66945 5.77642 8.75946C5.86643 8.84944 5.9885 8.9 6.11578 8.9C6.24306 8.9 6.36513 8.84944 6.45514 8.75946L8.37514 6.83946C8.46513 6.74944 8.51568 6.62738 8.51568 6.5001C8.51568 6.37282 8.46513 6.25075 8.37514 6.16074L6.45514 4.24074C6.36461 4.1533 6.24336 4.10492 6.11751 4.10601C5.99165 4.10711 5.87126 4.15759 5.78227 4.24658C5.69327 4.33558 5.64279 4.45597 5.6417 4.58183C5.6406 4.70768 5.68899 4.82893 5.77642 4.91946L7.35706 6.5001L5.77642 8.08074C5.68644 8.17075 5.63588 8.29282 5.63588 8.4201Z"
                    fill="#F16B7C"
                  />
                </svg>
              </div>
              <p>{name}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const Menubar = (props) => {
  // var backgroundOpacity = 0.0;

  const { searchKeyword = '', menubarOpacity = 1, ...restProps } = props;
  const {
    showMobileMoreModal,
    setShowAppDownloadModal,
    showAppDownloadBanner,
    setShowAppDownloadBanner,
    setMenubarHeight,
  } = useGlobalConfig();
  const { isLg } = useRWD();

  const [showSearchModal, setShowSearchModal] = useState(false);
  // const searchAreaRef = useRef(null);

  const menubarRef = useRef(null);

  useEffect(() => {
    if (!menubarRef.current) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      setMenubarHeight(entry.contentRect.height);
      // console.log('menubarHeight', entry.contentRect.height);
    });
    resizeObserver.observe(menubarRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const {
    isSignInStatusReady,
    user: sorraUser,
    getAuthorizationHeader,
  } = useUserAuth();

  const {
    notifications,
    categoryTree,
    topSearches: defaultTopSearches,
  } = useAppLaunch();

  // const notificationsLength = notifications.length;

  const [mobilePopularMakeupExpand, setMobilePopularMakeupExpand] =
    useState(false);
  const [mobilePopularSkincareExpand, setMobilePopularSkincareExpand] =
    useState(false);

  // const isHoveringPopularMakeup = useHoverDirty(popularMakeupRef);
  const [showPopularMakeupFloating, setShowPopularMakeupFloating] =
    useState(false);
  const [showPopularSkincareFloating, setShowPopularSkincareFloating] =
    useState(false);

  const popularMakeupCategories = _.reduce(
    ['c', 'd', 'i'],
    (acc, s) => {
      return acc.concat(
        (
          _.find(categoryTree, (c) => {
            return _.toLower(c.alias) === s;
          }) || {}
        ).second_categories || []
      );
    },
    []
  );

  const popularSkincareCategories = _.reduce(
    ['a'],
    (acc, s) => {
      return acc.concat(
        (
          _.find(categoryTree, (c) => {
            return _.toLower(c.alias) === s;
          }) || {}
        ).second_categories || []
      );
    },
    []
  );

  const {
    refs: popularMakeupRefs,
    floatingStyles: popularMakeupFloatingStyles,
    context: popularMakeupFloatingContext,
  } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8)],
    open: showPopularMakeupFloating,
    onOpenChange: setShowPopularMakeupFloating,
  });
  const popularMakeupHover = useHover(popularMakeupFloatingContext, {
    handleClose: safePolygon(),
  });
  const {
    getReferenceProps: popularMakeupReferenceProps,
    getFloatingProps: popularMakeupFloatingProps,
  } = useInteractions([popularMakeupHover]);

  const {
    refs: popularSkincareRefs,
    floatingStyles: popularSkincareFloatingStyles,
    context: popularSkincareFloatingContext,
  } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8)],
    open: showPopularSkincareFloating,
    onOpenChange: setShowPopularSkincareFloating,
  });
  const popularSkincareHover = useHover(popularSkincareFloatingContext, {
    handleClose: safePolygon(),
  });
  const {
    getReferenceProps: popularSkincareReferenceProps,
    getFloatingProps: popularSkincareFloatingProps,
  } = useInteractions([popularSkincareHover]);

  const router = useRouter();
  const { asPath, pathname, query } = router;

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(18)],
  });

  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const { voucherQuantity } = useUserData();
  const navs = useNavConfig(voucherQuantity);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        refs.floating.current &&
        !refs.floating.current.contains(event.target) &&
        !refs.reference.current.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mouseup', handleClickOutside);
    document.addEventListener('touchend', handleClickOutside);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (sorraUser) {
      const { phone_verified, post_signup_questionnaire_filled } = sorraUser;

      if (!phone_verified) {
        alert('請完成註冊程序。');
        window.location = '/phone';
        return;
      }

      // if (phone_verified && !post_signup_questionnaire_filled) {
      //   alert('請完成註冊程序。');
      //   window.location = '/question1';
      // }
    }
  }, [sorraUser]);

  return (
    <div
      style={{
        zIndex:
          showPopularMakeupFloating || showPopularSkincareFloating ? 60 : 40,
      }}
      className={classnames('fixed left-0 w-full', {
        'top-0': !notifications.length,
        'top-[45px]': notifications.length,
      })}
    >
      {notifications.length > 0 && (
        <div className="fixed top-0 z-40 h-[45px] w-full bg-white text-sm text-white">
          <Swiper
            className="h-full w-full"
            spaceBetween={0}
            slidesPerView={1}
            modules={[EffectFade, Autoplay]}
            effect="fade"
            loop={true}
            autoplay={{
              delay: 2000,
              pauseOnMouseEnter: true,
            }}
          >
            {notifications.map((notification) => {
              const { id, url, text } = notification;
              return (
                <SwiperSlide key={id} className="h-full w-full bg-sorra-blue">
                  <div className="flex h-full w-full items-center">
                    <div className="w-full overflow-hidden truncate text-center">
                      <a href={url} target="_blank">
                        {text}
                      </a>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}

      <nav
        ref={menubarRef}
        className={'relative w-full justify-center border-b'}
        style={{
          backgroundColor: 'rgba(255, 255, 255, ' + menubarOpacity + ')',
          borderBottomColor: 'rgba(209, 219, 239, ' + menubarOpacity + ')',
        }}
      >
        {showAppDownloadBanner && (
          <div className="flex w-full items-center gap-x-2 bg-white py-3 pl-4 pr-2 lg:hidden">
            <div className="h-[33px] w-[33px] overflow-hidden rounded-lg">
              <img src="/images/app.png" className="h-full w-full" />
            </div>
            <div className="grow">
              <p className="text-xs font-bold text-black">
                Sorra - 最真實美妝評論平台
              </p>
              <p className="text-[10px] text-[#9B9B9B]">
                Open in the Sorra app
              </p>
            </div>
            <button
              className="rounded-lg border border-sorra-pink px-3 py-2 text-xs font-medium text-sorra-pink"
              onClick={() => {
                openAppOrStore();
              }}
            >
              開啟應該程式
            </button>
            <button
              className="h-6 w-6 cursor-pointer"
              onClick={() => {
                setShowAppDownloadBanner(false);
                sessionStore.setShowAppDownloadBanner(false);
              }}
            >
              <CrossSvg color="#4A4A4A" />
            </button>
          </div>
        )}
        <MaxWidthContainer className="relative hidden h-full p-4 lg:flex">
          <div className="relative flex h-full w-full items-center text-sm text-[#111928]">
            <button
              className="mr-9 shrink-0"
              onClick={() => {
                router.push('/');
              }}
            >
              <img
                className="h-[22px] w-[133px] lg:h-[25px] lg:w-[149px]"
                alt="sorra"
                src="/images/sorra.png"
              />
            </button>

            {/* 熱門美妝 熱門護膚 AI成份分析 兌換禮物 試用活動 */}
            <div
              className={classnames('mr-9 shrink-0 items-center gap-x-4', {
                hidden: showSearchModal,
                flex: !showSearchModal,
              })}
            >
              <div className="relative">
                <Link
                  prefetch={false}
                  href="/ranking?tab=c"
                  className="hover:text-sorra-pink"
                  ref={popularMakeupRefs.setReference}
                  {...popularMakeupReferenceProps()}
                >
                  美妝排行
                </Link>
              </div>
              <Link
                prefetch={false}
                href="/ranking?tab=a"
                className="hover:text-sorra-pink"
                ref={popularSkincareRefs.setReference}
                {...popularSkincareReferenceProps()}
              >
                熱門護膚
              </Link>
              <Link
                prefetch={false}
                href="/ingredients"
                className="hover:text-sorra-pink"
              >
                AI成份分析
              </Link>
              <div className="h-4 w-px bg-[#9B9B9B]" />
              <Link
                prefetch={false}
                href="/rewards"
                className="relative hover:text-sorra-pink"
              >
                兌換禮物
                <img
                  src="/images/icons/vip.png"
                  className="absolute right-0 top-0 h-[10px] w-[18px] -translate-y-[100%] translate-x-[80%] object-contain"
                />
              </Link>
              <Link
                prefetch={false}
                href="/campaign"
                className="relative hover:text-sorra-pink"
              >
                試用活動
                <img
                  src="/images/icons/vip.png"
                  className="absolute right-0 top-0 h-[10px] w-[18px] -translate-y-[100%] translate-x-[80%] object-contain"
                />
              </Link>
              <Link
                prefetch={false}
                href={`${sorraUser ? '/profile' : ''}/memberscontent`}
                className="relative hover:text-sorra-pink"
              >
                會員專屬內容
                <img
                  src="/images/icons/vip.png"
                  className="absolute right-0 top-0 h-[10px] w-[18px] -translate-y-[100%] translate-x-[80%] object-contain"
                />
              </Link>

              {showPopularMakeupFloating && (
                <FloatingCategories
                  ref={popularMakeupRefs.setFloating}
                  style={{ ...popularMakeupFloatingStyles }}
                  {...popularMakeupFloatingProps()}
                  categories={popularMakeupCategories}
                />
              )}
              {showPopularSkincareFloating && (
                <FloatingCategories
                  ref={popularSkincareRefs.setFloating}
                  style={{ ...popularSkincareFloatingStyles }}
                  {...popularSkincareFloatingProps()}
                  categories={popularSkincareCategories}
                />
              )}

              {/* {!sorraUser && <Link href="/membership">會員制度</Link>} */}
            </div>
            {isSignInStatusReady && (
              <div
                style={{
                  opacity: menubarOpacity,
                }}
                className={classnames('relative flex h-[54px]', {
                  'ml-auto w-[320px]': !showSearchModal,
                  'w-auto grow': showSearchModal,
                  'pointer-events-none': menubarOpacity < 1,
                  'pointer-events-auto': menubarOpacity >= 1,
                })}
              >
                <SearchInput
                  showSearchModal={showSearchModal}
                  setShowSearchModal={setShowSearchModal}
                />
              </div>
            )}

            {isSignInStatusReady && (
              <div className="ml-6 flex shrink-0 items-center gap-x-7">
                <Link
                  prefetch={false}
                  href="/membership"
                  className="ml-auto flex items-center gap-x-1 hover:text-sorra-pink"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                  >
                    <path
                      d="M9.32292 11.1667H8.65625V8.5H7.98958M8.65625 5.83333H8.66292M14.6563 8.5C14.6563 9.28793 14.5011 10.0681 14.1995 10.7961C13.898 11.5241 13.456 12.1855 12.8989 12.7426C12.3417 13.2998 11.6803 13.7417 10.9524 14.0433C10.2244 14.3448 9.44418 14.5 8.65625 14.5C7.86832 14.5 7.0881 14.3448 6.36015 14.0433C5.6322 13.7417 4.97076 13.2998 4.41361 12.7426C3.85646 12.1855 3.4145 11.5241 3.11297 10.7961C2.81144 10.0681 2.65625 9.28793 2.65625 8.5C2.65625 6.9087 3.28839 5.38258 4.41361 4.25736C5.53883 3.13214 7.06495 2.5 8.65625 2.5C10.2475 2.5 11.7737 3.13214 12.8989 4.25736C14.0241 5.38258 14.6563 6.9087 14.6563 8.5Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p>會員制度</p>
                </Link>
                <button
                  className="flex items-center justify-center gap-x-1 hover:text-sorra-pink"
                  onClick={() => {
                    setShowAppDownloadModal(true);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M8 12H8.00667M5.33333 14H10.6667C11.0203 14 11.3594 13.8595 11.6095 13.6095C11.8595 13.3594 12 13.0203 12 12.6667V3.33333C12 2.97971 11.8595 2.64057 11.6095 2.39052C11.3594 2.14048 11.0203 2 10.6667 2H5.33333C4.97971 2 4.64057 2.14048 4.39052 2.39052C4.14048 2.64057 4 2.97971 4 3.33333V12.6667C4 13.0203 4.14048 13.3594 4.39052 13.6095C4.64057 13.8595 4.97971 14 5.33333 14Z"
                      stroke="currentColor"
                      strokeWidth="1.33"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p>手機應用程式</p>
                </button>
                {sorraUser ? (
                  <div className="flex gap-x-2">
                    <button
                      ref={refs.setReference}
                      className="flex h-[41px] min-w-15 items-center justify-center rounded-[100px] border-[1px] border-[#E7E7E7] bg-[#FFFFFF] px-[8px] py-[4px] text-white"
                      onClick={() => {
                        setShowProfileMenu(!showProfileMenu);
                      }}
                    >
                      <div className="mr-2 h-[24px] w-[22px]">
                        <img
                          src="/images/sorra-coin.png"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <p className="text-sm text-black">
                        ${Math.max(sorraUser.score || 0, 0)}
                      </p>
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-x-4">
                    <button
                      className="menubar-normal-search-button flex h-[41px] items-center justify-center rounded-lg bg-sorra-pink px-5 text-white"
                      onClick={() => {
                        MixpanelConfig.track('signinup_from_nav');
                        redirectToLogin(asPath);
                      }}
                    >
                      <img src="/svgs/user-w.svg"></img>
                      <p className="text-sm text-white">會員登陸</p>
                    </button>
                  </div>
                )}
                {showProfileMenu && (
                  <div
                    ref={refs.setFloating}
                    style={{
                      ...floatingStyles,
                      boxShadow: '0px 4px 50px 0px rgba(0, 0, 0, 0.10)',
                    }}
                    className="flex min-w-[234px] flex-col gap-y-3 rounded-lg bg-white px-[30px] py-[22px]"
                  >
                    <Link
                      prefetch={false}
                      className="text-left"
                      href={`/p/${sorraUser?.referral_code}`}
                      onClick={() => {
                        setShowProfileMenu(false);
                      }}
                    >
                      我的美妝檔案
                    </Link>
                    <div className="flex flex-col gap-y-1">
                      <Link
                        prefetch={false}
                        className="text-left"
                        href="/profile"
                        onClick={() => {
                          setShowProfileMenu(false);
                        }}
                      >
                        個人中心
                      </Link>
                      <div className="mt-3 flex flex-col items-start gap-2 self-stretch rounded-lg bg-[#F5F5F5] p-4">
                        {navs.map((nav, idx) => {
                          return (
                            <div
                              key={idx}
                              className="flex w-full flex-col gap-y-2"
                            >
                              {nav.map((item, idx) => {
                                const { quantity } = item;
                                return (
                                  <button
                                    key={idx}
                                    className={classnames(
                                      'flex h-6 min-w-[200px] items-center gap-2 self-stretch stroke-[#111928] text-[#111928] transition-colors hover:stroke-sorra-pink hover:text-sorra-pink',
                                      {
                                        'lg:!stroke-sorra-pink lg:!text-sorra-pink':
                                          pathname === item.path,
                                      }
                                    )}
                                    onClick={() => {
                                      item.handler && item.handler();
                                    }}
                                  >
                                    <div className="flex aspect-square h-5 w-5 items-center justify-center">
                                      {item.icon}
                                    </div>
                                    <p
                                      className="ml-2 flex-1 text-left text-sm font-normal leading-[150%]"
                                      style={{ fontFamily: '"Noto Sans TC"' }}
                                    >
                                      {item.name}
                                    </p>
                                    {!_.isNil(quantity) && quantity > 0 && (
                                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-sorra-pink text-xs text-white">
                                        {quantity}
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                      <div className="lg:mt-5">
                        <Link
                          prefetch={false}
                          className="group flex items-center gap-x-2 text-left text-[#111928]"
                          href="http://wa.me/85264735443"
                          target="_blank"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                          >
                            <path
                              d="M6.66667 8.83594H6.675M10 8.83594H10.0083M13.3333 8.83594H13.3417M7.5 13.8359H4.16667C3.72464 13.8359 3.30072 13.6603 2.98816 13.3478C2.67559 13.0352 2.5 12.6113 2.5 12.1693V5.5026C2.5 5.06058 2.67559 4.63665 2.98816 4.32409C3.30072 4.01153 3.72464 3.83594 4.16667 3.83594H15.8333C16.2754 3.83594 16.6993 4.01153 17.0118 4.32409C17.3244 4.63665 17.5 5.06058 17.5 5.5026V12.1693C17.5 12.6113 17.3244 13.0352 17.0118 13.3478C16.6993 13.6603 16.2754 13.8359 15.8333 13.8359H11.6667L7.5 18.0026V13.8359Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <p>聯絡我們 (WhatsApp)</p>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </MaxWidthContainer>

        <MaxWidthContainer
          className={
            'mx-auto flex h-full flex-wrap items-center justify-between p-4 lg:hidden'
            // (restProps.props.sourceUI == 'Search' ? ' hidden' : '')
          }
        >
          <button
            className="flex h-full items-center"
            onClick={() => {
              router.push('/');
            }}
          >
            <img
              className="h-[22px] w-[133px] lg:h-[25px] lg:w-[149px]"
              alt="sorra"
              src="/images/sorra.png"
            />
          </button>
          {/* </a> */}

          <div className="flex h-full items-center">
            <div className="relative flex items-center gap-x-6">
              {/* <button
                onClick={() => {
                  pressSearch();
                }}
              >
                <img src="/svgs/search.svg"></img>
              </button> */}
              <div
                className={classnames('flex gap-x-4', {
                  hidden: !isSignInStatusReady,
                  flex: isSignInStatusReady,
                })}
              >
                <button
                  className="flex items-center justify-center"
                  onClick={() => {
                    setShowSearchModal(true);
                  }}
                >
                  <div className="h-6 w-6">
                    <MicroscopeSvg />
                  </div>
                </button>
                {!sorraUser ? (
                  <button
                    className="menubar-normal-search-button flex h-[41px] items-center justify-center rounded-lg bg-sorra-pink px-3 text-white"
                    onClick={() => {
                      redirectToLogin(asPath);
                    }}
                  >
                    <img src="/svgs/user-w.svg"></img>
                    <p className="text-sm text-white">會員登陸</p>
                  </button>
                ) : (
                  <button
                    className="flex h-[41px] items-center justify-center rounded-[100px] border-[1px] border-[#E7E7E7] bg-[#FFFFFF] px-[8px] py-[4px] text-white"
                    onClick={
                      /*firSignout*/ () => {
                        window.location = '/profile';
                      }
                    }
                  >
                    <div className="mr-2 h-[24px] w-[22px]">
                      <img
                        src="/images/sorra-coin.png"
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <p className="text-sm text-black">
                      ${Math.max(sorraUser.score || 0, 0)}
                    </p>
                  </button>
                )}
              </div>
            </div>
          </div>
        </MaxWidthContainer>
      </nav>
      <Modal
        isOpen={showMobileMoreModal && !isLg}
        shouldCloseOnOverlayClick={false}
        className="h-full w-full bg-white"
        overlayClassName="z-50 absolute flex items-center justify-center top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
      >
        <div className="h-full w-full overflow-y-auto px-4 pb-6">
          <div className="flex items-center py-6">
            <img
              className="h-[22px] w-[133px] lg:h-[25px] lg:w-[149px]"
              alt="sorra"
              src="/images/sorra.png"
            />
            <button
              className="ml-auto rounded-lg border border-[#E7E7E7] bg-[#FFFFFF] p-2.5"
              onClick={() => setShowMobileMoreModal(false)}
            >
              <div className="h-5 w-5">
                <CrossSvg />
              </div>
            </button>
          </div>
          <div className="">
            <MobileCategories
              title="熱門美妝"
              categories={popularMakeupCategories}
              expand={mobilePopularMakeupExpand}
              setExpand={setMobilePopularMakeupExpand}
              onNavigate={() => {
                setShowMobileMoreModal(false);
              }}
            />
          </div>
          <div className="">
            <MobileCategories
              title="熱門護膚"
              categories={popularSkincareCategories}
              expand={mobilePopularSkincareExpand}
              setExpand={setMobilePopularSkincareExpand}
              onNavigate={() => {
                setShowMobileMoreModal(false);
              }}
            />
          </div>

          <div className="border-b border-[#E7E7E7] py-4 text-base font-medium text-[#111928]">
            <Link
              prefetch={false}
              href="/ingredients"
              className="w-full"
              onClick={() => {
                setShowMobileMoreModal(false);
              }}
            >
              AI成份分析
            </Link>
          </div>
          <div className="border-b border-[#E7E7E7] py-4 text-base font-medium text-[#111928]">
            <Link
              prefetch={false}
              href="/rewards"
              className="flex w-full items-center gap-x-1"
              onClick={() => {
                setShowMobileMoreModal(false);
              }}
            >
              兌換禮物
              <img
                src="/images/icons/vip.png"
                className="h-[13px] w-[22px] object-contain"
              />
            </Link>
          </div>
          <div className="border-b border-[#E7E7E7] py-4 text-base font-medium text-[#111928]">
            <Link
              prefetch={false}
              href="/campaign"
              className="flex w-full items-center gap-x-1"
              onClick={() => {
                setShowMobileMoreModal(false);
              }}
            >
              試用活動
              <img
                src="/images/icons/vip.png"
                className="h-[13px] w-[22px] object-contain"
              />
            </Link>
          </div>
          <div className="border-b border-[#E7E7E7] py-4 text-base font-medium text-[#111928]">
            <Link
              prefetch={false}
              href="/membership"
              className="flex w-full items-center gap-x-1"
              onClick={() => {
                setShowMobileMoreModal(false);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M9.32292 11.1667H8.65625V8.5H7.98958M8.65625 5.83333H8.66292M14.6563 8.5C14.6563 9.28793 14.5011 10.0681 14.1995 10.7961C13.898 11.5241 13.456 12.1855 12.8989 12.7426C12.3417 13.2998 11.6803 13.7417 10.9524 14.0433C10.2244 14.3448 9.44418 14.5 8.65625 14.5C7.86832 14.5 7.0881 14.3448 6.36015 14.0433C5.6322 13.7417 4.97076 13.2998 4.41361 12.7426C3.85646 12.1855 3.4145 11.5241 3.11297 10.7961C2.81144 10.0681 2.65625 9.28793 2.65625 8.5C2.65625 6.9087 3.28839 5.38258 4.41361 4.25736C5.53883 3.13214 7.06495 2.5 8.65625 2.5C10.2475 2.5 11.7737 3.13214 12.8989 4.25736C14.0241 5.38258 14.6563 6.9087 14.6563 8.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p>會員制度</p>
            </Link>
          </div>
          <div className="border-b border-[#E7E7E7] py-4 text-base font-medium text-[#111928]">
            <p>個人中心</p>
            <div className="mt-3 flex flex-col items-start self-stretch rounded-lg bg-[#F5F5F5] p-3">
              {navs.map((nav, idx) => {
                return (
                  <div key={idx} className="flex w-full flex-col">
                    {nav.map((item, itemIdx) => {
                      const { quantity } = item;
                      return (
                        <button
                          key={itemIdx}
                          className={classnames(
                            'flex items-center self-stretch stroke-[#111928] py-2.5 text-[#111928] transition-colors hover:stroke-sorra-pink hover:text-sorra-pink'
                          )}
                          onClick={() => {
                            item.handler && item.handler();
                            setShowMobileMoreModal(false);
                          }}
                        >
                          <div className="flex aspect-square h-5 w-5 flex-shrink-0 items-center justify-center">
                            {item.icon}
                          </div>
                          <p
                            className="ml-2 flex-1 text-left text-sm font-normal leading-normal"
                            style={{ fontFamily: '"Noto Sans TC"' }}
                          >
                            {item.name}
                          </p>
                          {!_.isNil(quantity) && quantity > 0 && (
                            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sorra-pink text-xs text-white">
                              {quantity}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-base font-medium text-[#111928]">
              <Link
                prefetch={false}
                className="flex w-full items-center gap-x-1"
                href="http://wa.me/85264735443"
                target="_blank"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M6.66667 8.33594H6.675M10 8.33594H10.0083M13.3333 8.33594H13.3417M7.5 13.3359H4.16667C3.72464 13.3359 3.30072 13.1603 2.98816 12.8478C2.67559 12.5352 2.5 12.1113 2.5 11.6693V5.0026C2.5 4.56058 2.67559 4.13665 2.98816 3.82409C3.30072 3.51153 3.72464 3.33594 4.16667 3.33594H15.8333C16.2754 3.33594 16.6993 3.51153 17.0118 3.82409C17.3244 4.13665 17.5 4.56058 17.5 5.0026V11.6693C17.5 12.1113 17.3244 12.5352 17.0118 12.8478C16.6993 13.1603 16.2754 13.3359 15.8333 13.3359H11.6667L7.5 17.5026V13.3359Z"
                    stroke="#111928"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p>聯絡我們 (WhatsApp)</p>
              </Link>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Menubar;
